// libraries
import React, { useEffect, useId } from 'react'
import clsx from 'clsx'
import { useFormContext, useWatch, RegisterOptions } from 'react-hook-form'

// utils
import { isNonWhitespaceString, useAnnounceOwnError } from '/lib/reactutils/utils'

// svg
import { Warning } from '/react4xp/components/Svg/Ux/Ux'

interface TextAreaProps {
    id: string
    name: string
    label?: string
    placeholder?: string
    required?: boolean
    maxLength?: {
        limit: number
        text: string
    }
    minLength?: number
    value?: string
    disabled?: boolean
    className?: string
    errorMessages?: {
        required?: string
        maxLength?: string
        minLength?: string
    }
    helperText?: string
    onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
    onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void
}

export default function TextArea({
    id,
    name,
    label,
    placeholder,
    required,
    maxLength,
    minLength,
    value,
    disabled,
    className,
    errorMessages,
    helperText,
    onChange,
    onKeyDown
}: TextAreaProps) {
    const { register, setValue, control, formState: { errors, submitCount } } = useFormContext()

    let validations: RegisterOptions = {
        onChange: (e) => {
            setValue(name, e.target.value)
            if (onChange) onChange(e)
        }
    }

    if (required) {
        validations.required = errorMessages?.required
    }

    if (maxLength) {
        validations.maxLength = {
            value: maxLength.limit,
            message: errorMessages?.maxLength
        }
    }

    if (minLength) {
        validations.minLength = {
            value: minLength,
            message: errorMessages?.minLength
        }
    }

    useEffect(() => {
        if (value) {
            setValue(name, value)
        }
    }, [name, setValue, value])

    const watchedValue = useWatch({
        control,
        name,
        defaultValue: value,
    })

    const characterCount = watchedValue ? watchedValue.length : 0
    const errorMessage = errors[name]?.message
    const announceError = useAnnounceOwnError(Boolean(errorMessage), submitCount)

    const showHelperText = isNonWhitespaceString(helperText)
    const instanceId = useId()
    const helperTextId = `${instanceId}-helper-text`
    const errorMessageId = `${instanceId}-error-message`

    const describedBy = [
        showHelperText && helperTextId,
        errorMessage && errorMessageId
    ].filter(Boolean).join(' ') || undefined

    const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && onKeyDown) {
            onKeyDown(event)
        }
    }

    return (
        <div
            className={clsx(
                'form-line',
                className,
                errors[name] && 'form-line--error'
            )}
        >

            <label className='form-line__label text-md' htmlFor={id}>
                {label} {required && <span className='form-line__label__required text-sm'>(Required)</span>}
            </label>

            <div className='form-line__line-wrapper'>

                <textarea
                    {...register(name, validations)}
                    id={id}
                    name={name}
                    placeholder={placeholder}
                    className='form-line__textarea text-md'
                    disabled={disabled || false}
                    maxLength={maxLength?.limit || undefined}
                    minLength={minLength}
                    onKeyDown={handleKeyPress}
                    aria-required={required || undefined}
                    aria-invalid={Boolean(errorMessage)}
                    aria-describedby={describedBy}
                ></textarea>

                {maxLength && (
                    <p className='form-line__counter'>
                        {characterCount}/{maxLength.limit} {maxLength.text}
                    </p>
                )}

            </div>

            {showHelperText && (
                <div
                    id={helperTextId}
                    className='form-line__help-text text-sm'
                    dangerouslySetInnerHTML={{ __html: helperText }}
                    suppressHydrationWarning
                />
            )}

            {errorMessage && (
                <p
                    id={errorMessageId}
                    role={announceError ? 'alert' : undefined}
                    className='
                        form-line__error-message
                        form-line__error-message--input
                        labels-sm-regular
                    '
                >
                    <Warning />
                    {typeof errorMessage === 'string' && errorMessage}
                </p>
            )}

        </div>
    )
}